import React, { useState } from "react";
import { IClubhouseCOIData } from "../../types/forms";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { defaultDateFormat, displayFormAlert } from "../../lib/utils";
import FileUpload from "../../components/FileUpload";
import { VALID_FILE_EXTENSIONS } from "../../lib/globals";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { selectSelectedTeam } from "../../features/clubhouse/clubhouseSlice";
import { alertActions } from "../../features/alert/alertSlice";
import { getCOI, updateCOI } from "../../lib/api";
import { IAPIResponse, ICOIInfo } from "../../types/api";
import { useLoaderData } from "react-router";

export const loader = async ({ params }: any) => {
    const { teamId } = params;
    const coi = await getCOI(teamId);

    return {
        teamId,
        coi,
    };
};

const ClubhouseCOI = (): React.JSX.Element => {
    const team = useAppSelector(selectSelectedTeam);
    const dispatch = useAppDispatch();
    const {
        coi: { coi: loadedCOI },
    } = useLoaderData() as { coi: IAPIResponse & { coi: ICOIInfo[] } };
    const allowedExtensions = VALID_FILE_EXTENSIONS.file.slice();
    const [fileList, setFileList] = useState<File[]>([]);
    const [coi, setCoi] = useState<ICOIInfo>(loadedCOI[0]);

    const methods = useForm<IClubhouseCOIData>({
        mode: "onChange",
    });

    const onSubmit: SubmitHandler<IClubhouseCOIData> = async data => {
        if (fileList.length === 0) {
            dispatch(
                alertActions.error({
                    title: `No file submitted`,
                    dismissible: true,
                }),
            );
            return;
        }

        await updateCOI(team!.id, fileList)
            .then(response => {
                dispatch(
                    alertActions.success({
                        title: `COI uploaded successfully`,
                        dismissible: true,
                    }),
                );

                setCoi(response.coi);
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem uploading the COI.`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            })
            .finally(() => {
                setFileList([]);
                methods.resetField("files");
            });
    };

    return (
        <section className="teamCOIInfo">
            <h2>Certificate of Insurance</h2>
            <p>
                All teams must upload their COI prior to their first game. The
                COI must list Baseball365 Inc. as a Certificate Holder or an
                Additional Insured. If your insurance company needs our address
                it is:
            </p>
            <p>
                {" "}
                Baseball365
                <br />
                PO Box 5614
                <br />
                Madison, WI 53705
            </p>
            <p>
                Any questions concerning your COI, please contact{" "}
                <a href="mailto:emma@majorybl.com">Emma.</a>
            </p>
            <p>
                <b>Please Note:</b> Uploading a new COI will <b>overwrite</b>{" "}
                any existing COI you have previously uploaded.
            </p>
            <FormProvider {...methods}>
                <Form
                    onSubmit={methods.handleSubmit(onSubmit)}
                    id="clubhouseTeamCOIForm"
                >
                    <Row>
                        <Col>
                            <Form.Label>Certificate of Insurance</Form.Label>
                            <Form.Group controlId="files">
                                <FileUpload
                                    maxFileCount={1}
                                    allowedFileExtensions={allowedExtensions}
                                    maxFileSize={5120000}
                                    fileList={fileList}
                                    setFileList={setFileList}
                                />
                                <Form.Label className="small">
                                    Accepted file types:{" "}
                                    {allowedExtensions.join(", ")}
                                    <br />
                                    Max file size: 5 MB
                                </Form.Label>
                            </Form.Group>
                            {methods.formState.errors.files &&
                                displayFormAlert({
                                    level: "danger",
                                    text: methods.formState.errors.files
                                        .message as string,
                                })}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button variant="primary" type="submit">
                                Upload
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </FormProvider>
            {coi && (
                <Table className="coiInfoTable">
                    <thead>
                        <tr>
                            <th>File Name</th>
                            <th>Upload Date</th>
                            <th>Status</th>
                            <th>
                                {coi.status === "Rejected"
                                    ? "Rejection"
                                    : "Approval"}{" "}
                                Date
                            </th>
                            {coi.status === "Rejected" && <th>Reason</th>}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{coi.file}</td>
                            <td>
                                {defaultDateFormat.format(
                                    new Date(coi.uploadDate),
                                )}
                            </td>
                            <td>{coi.status}</td>
                            <td>
                                {coi.approvalDate &&
                                    defaultDateFormat.format(
                                        new Date(coi.approvalDate),
                                    )}
                            </td>
                            {coi.status === "Rejected" && (
                                <td>{coi.message}</td>
                            )}
                        </tr>
                    </tbody>
                </Table>
            )}
        </section>
    );
};

export default ClubhouseCOI;
